import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Upload, FileSpreadsheet, CheckCircle, XCircle, AlertTriangle, 
  Download, Loader2, FileText, Calendar, User, Car, Building, 
  Fuel, History, Eye, Printer, RefreshCw
} from 'lucide-react';

export default function ImportacaoPage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [activeTab, setActiveTab] = useState('import'); // 'import' | 'logs'
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    if (activeTab === 'logs') {
      fetchLogs();
    }
  }, [activeTab]);

  const fetchLogs = async () => {
    try {
      const response = await api.get('/import/logs');
      setLogs(response.data);
    } catch (error) {
      toast.error('Erro ao carregar logs');
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.xlsx')) {
      toast.error('Apenas arquivos .xlsx são aceitos');
      return;
    }

    setFile(selectedFile);
    setPreview(null);
    setImportResult(null);
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await api.post('/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setPreview(response.data);
      toast.success(`Arquivo carregado: ${response.data.total_rows} linhas encontradas`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao processar arquivo');
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/import/execute', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setImportResult(response.data);
      toast.success(`Importação concluída: ${response.data.imported_count} registros`);
      fetchLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro na importação');
    } finally {
      setImporting(false);
    }
  };

  const getFieldIcon = (field) => {
    switch (field) {
      case 'date': return <Calendar size={14} />;
      case 'funcionario': return <User size={14} />;
      case 'veiculo': return <Car size={14} />;
      case 'secretaria': return <Building size={14} />;
      case 'combustivel': return <Fuel size={14} />;
      case 'posto': return <Building size={14} />;
      default: return <FileText size={14} />;
    }
  };

  const getFieldLabel = (field) => {
    const labels = {
      date: 'Data',
      codigo_abastecimento: 'Código CA',
      funcionario: 'Funcionário',
      veiculo: 'Veículo',
      secretaria: 'Secretaria',
      posto: 'Posto',
      combustivel: 'Combustível',
      quantidade: 'Quantidade',
      valor_unitario: 'Valor Unit.',
      ficha_interna: 'Ficha Interna',
      odometer_start: 'KM Inicial',
      odometer_end: 'KM Final'
    };
    return labels[field] || field;
  };

  const viewLogDetail = async (logId) => {
    try {
      const response = await api.get(`/import/logs/${logId}`);
      setSelectedLog(response.data);
    } catch (error) {
      toast.error('Erro ao carregar detalhes');
    }
  };

  return (
    <div className="space-y-6" data-testid="importacao-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#003366] to-[#0066CC] rounded-xl p-6 text-white">
        <h2 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Agente Inteligente de Importação
        </h2>
        <p className="text-blue-100">
          Importe planilhas Excel automaticamente. O sistema identifica colunas e cria registros não cadastrados.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <Button
          variant={activeTab === 'import' ? 'default' : 'outline'}
          onClick={() => setActiveTab('import')}
          className={activeTab === 'import' ? 'bg-[#003366]' : ''}
        >
          <Upload size={18} className="mr-2" />
          Importar Planilha
        </Button>
        <Button
          variant={activeTab === 'logs' ? 'default' : 'outline'}
          onClick={() => setActiveTab('logs')}
          className={activeTab === 'logs' ? 'bg-[#003366]' : ''}
        >
          <History size={18} className="mr-2" />
          Logs de Importação
        </Button>
      </div>

      {activeTab === 'import' && (
        <>
          {/* Upload Area */}
          <Card className="border-2 border-dashed border-slate-300 dark:border-slate-600 hover:border-[#0066CC] transition-colors">
            <CardContent className="p-8">
              <div className="flex flex-col items-center justify-center text-center">
                <FileSpreadsheet className="h-16 w-16 text-[#0066CC] mb-4" />
                <h3 className="text-xl font-semibold mb-2 text-slate-700 dark:text-slate-200">
                  {file ? file.name : 'Selecione uma planilha Excel'}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mb-4">
                  Formatos aceitos: .xlsx
                </p>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".xlsx"
                    onChange={handleFileChange}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Button asChild disabled={loading}>
                    <span className="bg-[#0066CC] hover:bg-[#0052A3]">
                      {loading ? (
                        <>
                          <Loader2 className="animate-spin mr-2" size={18} />
                          Processando...
                        </>
                      ) : (
                        <>
                          <Upload className="mr-2" size={18} />
                          Selecionar Arquivo
                        </>
                      )}
                    </span>
                  </Button>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Preview */}
          {preview && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-[#003366] dark:text-blue-400">
                  <Eye size={24} />
                  Pré-visualização da Importação
                </CardTitle>
                <CardDescription>
                  {preview.total_rows} linhas encontradas • {Object.keys(preview.column_mapping).length} colunas mapeadas
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Column Mapping */}
                <div>
                  <h4 className="font-semibold mb-3 text-slate-700 dark:text-slate-200">Mapeamento de Colunas</h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                    {Object.entries(preview.column_mapping).map(([header, mapping]) => (
                      <div 
                        key={header} 
                        className="flex items-center gap-2 p-2 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-800"
                      >
                        <CheckCircle className="text-green-600 flex-shrink-0" size={16} />
                        <div className="min-w-0">
                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{header}</p>
                          <p className="font-medium text-sm flex items-center gap-1 text-green-700 dark:text-green-400">
                            {getFieldIcon(mapping.field)}
                            {getFieldLabel(mapping.field)}
                          </p>
                        </div>
                      </div>
                    ))}
                    {preview.unmapped_columns.map((col) => (
                      <div 
                        key={col} 
                        className="flex items-center gap-2 p-2 bg-amber-50 dark:bg-amber-900/30 rounded-lg border border-amber-200 dark:border-amber-800"
                      >
                        <AlertTriangle className="text-amber-600 flex-shrink-0" size={16} />
                        <div className="min-w-0">
                          <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{col}</p>
                          <p className="font-medium text-sm text-amber-700 dark:text-amber-400">Não mapeado</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Preview Data */}
                <div>
                  <h4 className="font-semibold mb-3 text-slate-700 dark:text-slate-200">Prévia dos Dados (5 primeiras linhas)</h4>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {preview.headers.map((h, i) => (
                            <TableHead key={i} className="text-xs whitespace-nowrap">{h}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {preview.preview_data.map((row, i) => (
                          <TableRow key={i}>
                            {preview.headers.map((h, j) => (
                              <TableCell key={j} className="text-sm py-2">
                                {row[h] != null ? String(row[h]) : '-'}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Import Button */}
                <div className="flex justify-end gap-4">
                  <Button
                    variant="outline"
                    onClick={() => { setFile(null); setPreview(null); setImportResult(null); }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={handleImport}
                    disabled={importing}
                    className="bg-[#10B981] hover:bg-[#059669]"
                    data-testid="import-button"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="animate-spin mr-2" size={18} />
                        Importando...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="mr-2" size={18} />
                        Importar {preview.total_rows} Registros
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Import Result */}
          {importResult && (
            <Card className="border-l-4 border-l-green-500">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-700 dark:text-green-400">
                  <CheckCircle size={24} />
                  Importação Concluída
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-green-700 dark:text-green-400">{importResult.imported_count}</p>
                    <p className="text-sm text-green-600 dark:text-green-500">Importados</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/30 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-red-700 dark:text-red-400">{importResult.error_count}</p>
                    <p className="text-sm text-red-600 dark:text-red-500">Erros</p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-blue-700 dark:text-blue-400">
                      {importResult.created_entities.funcionarios + importResult.created_entities.veiculos}
                    </p>
                    <p className="text-sm text-blue-600 dark:text-blue-500">Func./Veículos Criados</p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/30 p-4 rounded-lg text-center">
                    <p className="text-3xl font-bold text-purple-700 dark:text-purple-400">
                      {importResult.created_entities.secretarias + importResult.created_entities.postos}
                    </p>
                    <p className="text-sm text-purple-600 dark:text-purple-500">Secret./Postos Criados</p>
                  </div>
                </div>

                {/* Logs */}
                <div className="bg-slate-900 dark:bg-slate-950 rounded-lg p-4 max-h-96 overflow-y-auto font-mono text-sm">
                  {importResult.logs.map((log, i) => (
                    <div 
                      key={i} 
                      className={`py-1 ${
                        log.includes('✅') ? 'text-green-400' : 
                        log.includes('❌') ? 'text-red-400' : 
                        log.includes('⚠️') ? 'text-yellow-400' :
                        log.includes('📈') ? 'text-cyan-400 font-bold' :
                        'text-slate-300'
                      }`}
                    >
                      {log}
                    </div>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex gap-4">
                  <Button
                    onClick={() => { setFile(null); setPreview(null); setImportResult(null); }}
                    className="bg-[#003366]"
                  >
                    <RefreshCw className="mr-2" size={18} />
                    Nova Importação
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => window.location.href = '/abastecimentos'}
                  >
                    <Printer className="mr-2" size={18} />
                    Ver Abastecimentos
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Logs Tab */}
      {activeTab === 'logs' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Logs List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#003366] dark:text-blue-400">
                <History size={24} />
                Histórico de Importações
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[600px] overflow-y-auto">
                {logs.length === 0 ? (
                  <p className="text-center text-slate-500 py-8">Nenhuma importação realizada</p>
                ) : (
                  logs.map((log) => (
                    <div
                      key={log.id}
                      onClick={() => viewLogDetail(log.id)}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                        selectedLog?.id === log.id 
                          ? 'bg-blue-50 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700' 
                          : 'bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <p className="font-medium text-slate-700 dark:text-slate-200 truncate">{log.filename}</p>
                        <span className="text-xs text-slate-500">{new Date(log.imported_at).toLocaleDateString('pt-BR')}</span>
                      </div>
                      <div className="flex gap-4 text-sm">
                        <span className="text-green-600">✅ {log.imported_count}</span>
                        <span className="text-red-600">❌ {log.error_count}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">Por: {log.imported_by}</p>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Log Detail */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#003366] dark:text-blue-400">
                <FileText size={24} />
                Detalhes da Importação
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedLog ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg">
                      <p className="text-xs text-slate-500">Arquivo</p>
                      <p className="font-medium truncate">{selectedLog.filename}</p>
                    </div>
                    <div className="bg-slate-50 dark:bg-slate-800 p-3 rounded-lg">
                      <p className="text-xs text-slate-500">Data</p>
                      <p className="font-medium">{new Date(selectedLog.imported_at).toLocaleString('pt-BR')}</p>
                    </div>
                    <div className="bg-green-50 dark:bg-green-900/30 p-3 rounded-lg">
                      <p className="text-xs text-green-600">Importados</p>
                      <p className="font-bold text-green-700 text-xl">{selectedLog.imported_count}</p>
                    </div>
                    <div className="bg-red-50 dark:bg-red-900/30 p-3 rounded-lg">
                      <p className="text-xs text-red-600">Erros</p>
                      <p className="font-bold text-red-700 text-xl">{selectedLog.error_count}</p>
                    </div>
                  </div>

                  {/* Created Entities */}
                  <div>
                    <h4 className="font-semibold mb-2 text-sm text-slate-700 dark:text-slate-200">Entidades Criadas:</h4>
                    <div className="flex flex-wrap gap-2">
                      <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded text-xs">
                        {selectedLog.created_entities?.secretarias || 0} Secretarias
                      </span>
                      <span className="px-2 py-1 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded text-xs">
                        {selectedLog.created_entities?.funcionarios || 0} Funcionários
                      </span>
                      <span className="px-2 py-1 bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 rounded text-xs">
                        {selectedLog.created_entities?.veiculos || 0} Veículos
                      </span>
                      <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900/50 text-orange-700 dark:text-orange-300 rounded text-xs">
                        {selectedLog.created_entities?.postos || 0} Postos
                      </span>
                      <span className="px-2 py-1 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded text-xs">
                        {selectedLog.created_entities?.combustiveis || 0} Combustíveis
                      </span>
                    </div>
                  </div>

                  {/* Logs */}
                  <div className="bg-slate-900 dark:bg-slate-950 rounded-lg p-3 max-h-64 overflow-y-auto font-mono text-xs">
                    {selectedLog.logs?.map((log, i) => (
                      <div 
                        key={i} 
                        className={`py-0.5 ${
                          log.includes('✅') ? 'text-green-400' : 
                          log.includes('❌') ? 'text-red-400' : 
                          log.includes('⚠️') ? 'text-yellow-400' :
                          'text-slate-300'
                        }`}
                      >
                        {log}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-center text-slate-500 py-8">
                  Selecione uma importação para ver os detalhes
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-white dark:from-blue-900/20 dark:to-slate-800 border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <h4 className="font-semibold text-blue-700 dark:text-blue-400 mb-2">Mapeamento Automático</h4>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              O sistema identifica colunas automaticamente usando algoritmos de similaridade.
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-green-50 to-white dark:from-green-900/20 dark:to-slate-800 border-l-4 border-l-green-500">
          <CardContent className="p-4">
            <h4 className="font-semibold text-green-700 dark:text-green-400 mb-2">Cadastro Inteligente</h4>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Funcionários, veículos e postos são criados automaticamente se não existirem.
            </p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-white dark:from-purple-900/20 dark:to-slate-800 border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <h4 className="font-semibold text-purple-700 dark:text-purple-400 mb-2">Validação Automática</h4>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Valores são validados e totais calculados automaticamente antes da importação.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
